import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Plus,
  X,
  Trash2,
  Loader2,
  NotebookPen,
  Save,
  ChevronLeft,
  Inbox,
  Send,
  MessageSquare,
  CheckCircle2,
  RotateCcw,
  CornerDownRight,
  Bot,
} from 'lucide-react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';

// ── Helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (isoString) => {
  if (!isoString) return '';
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// ── Badge colour mappings ────────────────────────────────────────────────────

const categoryBadge = {
  note: 'bg-violet-100 text-violet-700',
  thought: 'bg-purple-100 text-purple-700',
  'feature-request': 'bg-amber-100 text-amber-700',
  'action-item': 'bg-rose-100 text-rose-700',
};

const sourceBadge = {
  manual: 'bg-stone-100 text-stone-600',
  telegram: 'bg-blue-100 text-blue-700',
  melissa: 'bg-teal-100 text-teal-700',
};

const statusPill = {
  open: 'bg-emerald-100 text-emerald-700',
  in_progress: 'bg-sky-100 text-sky-700',
  awaiting_approval: 'bg-amber-100 text-amber-800',
  done: 'bg-stone-200 text-stone-500',
  dismissed: 'bg-red-100 text-red-600',
};

const statusLabel = {
  open: 'open',
  in_progress: 'in progress',
  awaiting_approval: 'needs your go-ahead',
  done: 'done',
  dismissed: 'dismissed',
};

// Who can write a note or a reply. Claude is never selectable by hand — it is
// stamped on replies written by the periodic job.
const AUTHORS = ['Nav', 'Kate', 'Codex'];
const AUTHOR_STORAGE_KEY = 'notesAuthor';

const authorBadge = {
  Nav: 'bg-indigo-100 text-indigo-700',
  Kate: 'bg-pink-100 text-pink-700',
  Codex: 'bg-cyan-100 text-cyan-700',
  Claude: 'bg-teal-100 text-teal-700',
};

const activityDotColor = {
  bingo: 'bg-orange-500',
  wedding_tasks: 'bg-rose-500',
  wedding_budget: 'bg-amber-500',
  expenses: 'bg-slate-500',
  notes: 'bg-violet-500',
};

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { key: 'notes', label: 'Inbox' },
  { key: 'activity', label: 'Activity' },
];

const STATUS_FILTERS = ['All', 'Open', 'Needs Go-Ahead', 'Done', 'Dismissed'];

// UI label -> API status value. 'Open' is expanded server-side to every
// in-flight status (open / in_progress / awaiting_approval).
const statusFilterValueMap = {
  Open: 'open',
  'Needs Go-Ahead': 'awaiting_approval',
  Done: 'done',
  Dismissed: 'dismissed',
};
const CATEGORY_FILTERS = ['All', 'Note', 'Thought', 'Feature Request', 'Action Item'];

const categoryValueMap = {
  Note: 'note',
  Thought: 'thought',
  'Feature Request': 'feature-request',
  'Action Item': 'action-item',
};

const categoryLabelMap = {
  note: 'Note',
  thought: 'Thought',
  'feature-request': 'Feature Request',
  'action-item': 'Action Item',
};

// ── Empty form factory ───────────────────────────────────────────────────────

const emptyForm = () => ({
  title: '',
  body: '',
  category: 'note',
  source: 'manual',
  status: 'open',
});

const readStoredAuthor = () => {
  try {
    const stored = localStorage.getItem(AUTHOR_STORAGE_KEY);
    return AUTHORS.includes(stored) ? stored : 'Nav';
  } catch {
    return 'Nav';
  }
};

// ── Main component ───────────────────────────────────────────────────────────

const NotesInterface = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Open');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [captureText, setCaptureText] = useState('');
  const [captureCategory, setCaptureCategory] = useState('note');
  const [captureSaving, setCaptureSaving] = useState(false);
  // Who is writing right now — remembered per browser so Kate, Nav and the
  // tester each keep their own identity without a separate login.
  const [author, setAuthor] = useState(readStoredAuthor);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySaving, setReplySaving] = useState(null);
  const [statusSaving, setStatusSaving] = useState(null);

  const deletedRef = useRef(null);
  const modalRef = useRef(null);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'All') params.set('status', statusFilterValueMap[statusFilter]);
      if (categoryFilter !== 'All') params.set('category', categoryValueMap[categoryFilter]);

      const res = await fetch(`${ENDPOINTS.NOTES}?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Failed to load notes');
      const data = await res.json();
      setNotes(data.notes ?? []);
      setActivity(data.activity ?? []);
    } catch (error) {
      console.error(error);
      toast.error('Could not load notes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem(AUTHOR_STORAGE_KEY, author);
    } catch {
      /* storage unavailable — author just resets next visit */
    }
  }, [author]);

  // ── Sorted activity ───────────────────────────────────────────────────────

  const sortedActivity = useMemo(
    () =>
      [...activity].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      ),
    [activity]
  );

  // ── CRUD operations ───────────────────────────────────────────────────────

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        body: form.body.trim() || null,
        category: form.category,
        source: form.source,
        author: editingNote ? form.author || null : author,
      };

      let res;
      if (editingNote) {
        res = await fetch(`${ENDPOINTS.NOTES}/${editingNote.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ ...body, status: form.status }),
        });
      } else {
        res = await fetch(ENDPOINTS.NOTES, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
        });
      }

      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Save failed');
      await fetchData();
      toast.success(editingNote ? 'Note updated.' : 'Note added.');
      closeModal();
    } catch {
      toast.error('Could not save note.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCapture = async (e) => {
    e.preventDefault();
    const text = captureText.trim();
    if (!text) {
      toast.error('Write something to capture.');
      return;
    }
    const [firstLine, ...rest] = text.split('\n');
    const title = firstLine.trim().slice(0, 120) || 'Captured note';
    const body = rest.join('\n').trim() || (text.length > title.length ? text : '');
    setCaptureSaving(true);
    try {
      const res = await fetch(ENDPOINTS.NOTES, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title,
          body,
          category: captureCategory,
          source: 'manual',
          author,
        }),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Capture failed');
      setCaptureText('');
      await fetchData();
      toast.success(
        captureCategory === 'feature-request'
          ? 'Captured. Claude will pick this up on its next pass.'
          : 'Captured to inbox.'
      );
    } catch {
      toast.error('Could not capture note.');
    } finally {
      setCaptureSaving(false);
    }
  };

  const handleDelete = async (note) => {
    try {
      const res = await fetch(`${ENDPOINTS.NOTES}/${note.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Delete failed');
      deletedRef.current = { ...note };
      await fetchData();

      toast(
        (t) => (
          <span className="flex items-center gap-3 text-sm">
            <span>Note deleted.</span>
            <button
              className="rounded-md bg-stone-900 px-3 py-1 text-xs font-semibold text-white"
              onClick={() => {
                toast.dismiss(t.id);
                handleUndo();
              }}
            >
              Undo
            </button>
          </span>
        ),
        { duration: 5000 }
      );

      setTimeout(() => {
        deletedRef.current = null;
      }, 5500);
    } catch {
      toast.error('Could not delete note.');
    }
  };

  const handleUndo = async () => {
    const note = deletedRef.current;
    if (!note) return;
    try {
      const { id, created_at, updated_at, ...body } = note; // eslint-disable-line no-unused-vars
      const res = await fetch(ENDPOINTS.NOTES, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Undo failed');
      deletedRef.current = null;
      await fetchData();
      toast.success('Note restored.');
    } catch {
      toast.error('Could not undo deletion.');
    }
  };

  // ── Thread + status actions ───────────────────────────────────────────────

  const handleReply = async (note) => {
    const draft = (replyDrafts[note.id] ?? '').trim();
    if (!draft) {
      toast.error('Write a reply first.');
      return;
    }
    setReplySaving(note.id);
    try {
      const res = await fetch(`${ENDPOINTS.NOTES}/${note.id}/replies`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ author, body: draft }),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Reply failed');
      setReplyDrafts((drafts) => ({ ...drafts, [note.id]: '' }));
      await fetchData();
    } catch {
      toast.error('Could not post reply.');
    } finally {
      setReplySaving(null);
    }
  };

  const handleStatusChange = async (note, status) => {
    setStatusSaving(note.id);
    try {
      const res = await fetch(`${ENDPOINTS.NOTES}/${note.id}/status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status, actor: author }),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Status change failed');
      await fetchData();
      toast.success(status === 'done' ? 'Marked resolved.' : 'Reopened.');
    } catch {
      toast.error('Could not update status.');
    } finally {
      setStatusSaving(null);
    }
  };

  const toggleThread = (noteId) =>
    setExpandedNoteId((current) => (current === noteId ? null : noteId));

  // ── Modal controls ────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingNote(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setForm({
      title: note.title ?? '',
      body: note.body ?? '',
      category: note.category ?? 'note',
      source: note.source ?? 'manual',
      status: note.status ?? 'open',
      author: note.author ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingNote(null);
    setForm(emptyForm());
  };

  // ── Focus trap + body scroll lock ─────────────────────────────────────────

  useEffect(() => {
    if (!modalOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const container = modalRef.current;
    if (container) {
      const focusable = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first) first.focus();

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeModal();
          return;
        }
        if (e.key !== 'Tab') return;
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      container.addEventListener('keydown', handleKeyDown);
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = prevOverflow;
      };
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-stone-50 to-white text-stone-900">
      <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6">

        {/* ── Header ── */}
        <div className="mb-5 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="shrink-0 rounded-full border border-stone-200 bg-white p-2 text-stone-500 transition hover:bg-stone-50 hover:text-stone-900"
            aria-label="Back to Home"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Inbox className="h-6 w-6 text-violet-600" />
            <h1 className="text-xl font-semibold text-stone-900 sm:text-2xl">Capture Inbox</h1>
          </div>
        </div>

        {/* ── Sticky tab bar ── */}
        <div className="sticky top-0 z-20">
          <div
            role="tablist"
            className="flex gap-2 overflow-x-auto rounded-[22px] border border-white/80 bg-white/85 p-2 shadow-sm ring-1 ring-stone-200 backdrop-blur"
          >
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={currentTab === key}
                onClick={() => setCurrentTab(key)}
                className={clsx(
                  'flex shrink-0 items-center gap-2 rounded-[16px] px-4 py-2.5 text-sm font-semibold transition',
                  currentTab === key
                    ? 'bg-stone-900 text-white shadow'
                    : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab content ── */}
        <div role="tabpanel" className="mt-5">
          {loading ? (
            <div className="flex items-center justify-center rounded-[20px] bg-white/75 p-16 shadow-sm ring-1 ring-stone-200">
              <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
            </div>
          ) : (
            <>
              {/* ════════════════════ Notes Tab ════════════════════ */}
              {currentTab === 'notes' && (
                <div>
                  <section className="mb-4 overflow-hidden rounded-[26px] border border-violet-100 bg-white/90 p-4 shadow-sm ring-1 ring-violet-100">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">
                      <Inbox className="h-4 w-4" />
                      Capture first, organize later
                    </div>
                    <p className="mt-2 text-sm leading-6 text-stone-500">
                      A lightweight inbox for thoughts, tasks, app ideas, reminders, and anything that should not disappear into chat.
                    </p>
                    {/* Who's writing — remembered per browser */}
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">
                        Posting as
                      </span>
                      {AUTHORS.map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setAuthor(name)}
                          aria-pressed={author === name}
                          className={clsx(
                            'rounded-full px-3 py-1.5 text-xs font-semibold transition',
                            author === name
                              ? 'bg-stone-900 text-white'
                              : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                          )}
                        >
                          {name}
                        </button>
                      ))}
                    </div>

                    <form onSubmit={handleQuickCapture} className="mt-3 grid gap-3">
                      <textarea
                        rows={3}
                        value={captureText}
                        onChange={(e) => setCaptureText(e.target.value)}
                        placeholder="Capture something quickly..."
                        className="w-full resize-none rounded-[20px] border border-violet-100 bg-violet-50/50 px-4 py-3 text-sm leading-6 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-violet-300 focus:bg-white focus:ring-2 focus:ring-violet-100"
                      />

                      {/* File it as you capture — feature requests reach Claude's queue */}
                      <div className="flex flex-wrap items-center gap-2">
                        {CATEGORY_FILTERS.filter((c) => c !== 'All').map((label) => {
                          const value = categoryValueMap[label];
                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => setCaptureCategory(value)}
                              aria-pressed={captureCategory === value}
                              className={clsx(
                                'rounded-full px-3 py-1.5 text-xs font-semibold transition',
                                captureCategory === value
                                  ? 'bg-violet-600 text-white'
                                  : 'border border-violet-100 bg-white text-stone-600 hover:bg-violet-50'
                              )}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="submit"
                        disabled={captureSaving}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-800 disabled:opacity-50 sm:w-auto"
                      >
                        {captureSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {captureSaving ? 'Capturing...' : 'Capture to inbox'}
                      </button>
                    </form>
                  </section>
                  {/* ── Filter pills ── */}
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    {/* Status pills */}
                    {STATUS_FILTERS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={clsx(
                          'shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold transition',
                          statusFilter === s
                            ? 'bg-stone-900 text-white'
                            : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                        )}
                      >
                        {s}
                      </button>
                    ))}

                    <span className="shrink-0 self-center text-stone-300">|</span>

                    {/* Category pills */}
                    {CATEGORY_FILTERS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setCategoryFilter(c)}
                        className={clsx(
                          'shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold transition',
                          categoryFilter === c
                            ? 'bg-stone-900 text-white'
                            : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                        )}
                      >
                        {c}
                      </button>
                    ))}
                  </div>

                  {/* ── Note cards ── */}
                  {notes.length === 0 ? (
                    <div className="rounded-[20px] border border-stone-200 bg-white/90 py-16 text-center shadow-sm backdrop-blur">
                      <NotebookPen className="mx-auto mb-3 h-10 w-10 text-stone-300 opacity-30" />
                      <p className="text-sm font-medium text-stone-400">No notes found.</p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {notes.map((note) => {
                        const isDone = note.status === 'done' || note.status === 'dismissed';
                        const catClass = categoryBadge[note.category] ?? 'bg-stone-100 text-stone-600';
                        const srcClass = sourceBadge[note.source] ?? 'bg-stone-100 text-stone-600';
                        const stClass = statusPill[note.status] ?? 'bg-stone-100 text-stone-600';
                        const authClass = authorBadge[note.author] ?? 'bg-stone-100 text-stone-600';
                        const replies = note.replies ?? [];
                        const isExpanded = expandedNoteId === note.id;
                        const needsGoAhead = note.status === 'awaiting_approval';

                        return (
                          <li
                            key={note.id}
                            className={clsx(
                              'rounded-[20px] border bg-white/90 p-4 shadow-sm backdrop-blur transition hover:shadow-md',
                              needsGoAhead ? 'border-amber-300 ring-1 ring-amber-100' : 'border-stone-200'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              {/* Content */}
                              <button
                                onClick={() => openEdit(note)}
                                className="min-w-0 flex-1 text-left"
                              >
                                <h3
                                  className={clsx(
                                    'text-sm font-semibold leading-snug',
                                    isDone ? 'text-stone-400 line-through' : 'text-stone-900'
                                  )}
                                >
                                  {note.title}
                                </h3>
                                {note.body && (
                                  <p className="mt-1 truncate text-xs text-stone-400">
                                    {note.body}
                                  </p>
                                )}
                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                  {note.author && (
                                    <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', authClass)}>
                                      {note.author}
                                    </span>
                                  )}
                                  <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', catClass)}>
                                    {categoryLabelMap[note.category] ?? note.category}
                                  </span>
                                  <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', srcClass)}>
                                    {note.source}
                                  </span>
                                  <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', stClass)}>
                                    {statusLabel[note.status] ?? note.status}
                                  </span>
                                  <span className="text-[11px] text-stone-400">
                                    {timeAgo(note.updated_at ?? note.created_at)}
                                  </span>
                                </div>
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleDelete(note)}
                                className="mt-0.5 shrink-0 text-stone-300 transition hover:text-red-500"
                                aria-label="Delete note"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            {/* ── Action row: thread toggle + resolve/reopen ── */}
                            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
                              <button
                                onClick={() => toggleThread(note.id)}
                                aria-expanded={isExpanded}
                                className={clsx(
                                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition',
                                  isExpanded
                                    ? 'bg-stone-900 text-white'
                                    : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                                )}
                              >
                                <MessageSquare className="h-3.5 w-3.5" />
                                {replies.length === 0
                                  ? 'Reply'
                                  : `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
                              </button>

                              {isDone ? (
                                <button
                                  onClick={() => handleStatusChange(note, 'open')}
                                  disabled={statusSaving === note.id}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-600 transition hover:bg-stone-50 disabled:opacity-50"
                                >
                                  {statusSaving === note.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  )}
                                  Reopen
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleStatusChange(note, 'done')}
                                  disabled={statusSaving === note.id}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
                                >
                                  {statusSaving === note.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                  )}
                                  Resolve
                                </button>
                              )}

                              {needsGoAhead && (
                                <span className="text-[11px] font-medium text-amber-700">
                                  Claude is waiting on your go-ahead
                                </span>
                              )}
                            </div>

                            {/* ── Thread ── */}
                            {isExpanded && (
                              <div className="mt-3 space-y-3">
                                {replies.length > 0 && (
                                  <ul className="space-y-2">
                                    {replies.map((reply) => {
                                      const isClaude = reply.author === 'Claude';
                                      return (
                                        <li
                                          key={reply.id}
                                          className={clsx(
                                            'rounded-2xl px-3.5 py-2.5',
                                            isClaude
                                              ? 'bg-teal-50 ring-1 ring-teal-100'
                                              : 'bg-stone-50 ring-1 ring-stone-100'
                                          )}
                                        >
                                          <div className="mb-1 flex items-center gap-1.5">
                                            {isClaude ? (
                                              <Bot className="h-3.5 w-3.5 text-teal-600" />
                                            ) : (
                                              <CornerDownRight className="h-3.5 w-3.5 text-stone-400" />
                                            )}
                                            <span
                                              className={clsx(
                                                'rounded-full px-2 py-0.5 text-[11px] font-medium',
                                                authorBadge[reply.author] ?? 'bg-stone-100 text-stone-600'
                                              )}
                                            >
                                              {reply.author}
                                            </span>
                                            <span className="text-[11px] text-stone-400">
                                              {timeAgo(reply.created_at)}
                                            </span>
                                          </div>
                                          <p className="whitespace-pre-wrap text-xs leading-5 text-stone-700">
                                            {reply.body}
                                          </p>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}

                                {/* Reply composer */}
                                <div className="flex items-end gap-2">
                                  <textarea
                                    rows={2}
                                    value={replyDrafts[note.id] ?? ''}
                                    onChange={(e) =>
                                      setReplyDrafts((drafts) => ({
                                        ...drafts,
                                        [note.id]: e.target.value,
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                        e.preventDefault();
                                        handleReply(note);
                                      }
                                    }}
                                    placeholder={`Reply as ${author}...`}
                                    className="w-full resize-none rounded-2xl border border-stone-200 px-3.5 py-2.5 text-xs leading-5 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                                  />
                                  <button
                                    onClick={() => handleReply(note)}
                                    disabled={replySaving === note.id}
                                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-900 text-white transition hover:bg-stone-800 disabled:opacity-50"
                                    aria-label="Post reply"
                                  >
                                    {replySaving === note.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* ── Floating Add button ── */}
                  <button
                    onClick={openAdd}
                    className="fixed bottom-6 right-6 z-30 inline-flex h-14 w-14 items-center justify-center rounded-full bg-stone-900 text-white shadow-lg transition hover:bg-stone-800 sm:bottom-8 sm:right-8"
                    aria-label="Add Note"
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                </div>
              )}

              {/* ════════════════════ Activity Tab ════════════════════ */}
              {currentTab === 'activity' && (
                <div>
                  <p className="mb-4 text-sm leading-relaxed text-stone-500">
                    A live feed of changes across all your apps — expenses logged, bingo squares completed, notes updated, and more.
                  </p>
                  {sortedActivity.length === 0 ? (
                    <div className="rounded-[20px] border border-stone-200 bg-white/90 py-16 text-center shadow-sm backdrop-blur">
                      <p className="text-sm font-medium text-stone-400">No activity yet.</p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {sortedActivity.map((entry, idx) => {
                        const dotClass = activityDotColor[entry.app] ?? 'bg-stone-400';
                        return (
                          <li
                            key={entry.id ?? idx}
                            className="rounded-[20px] border border-stone-200 bg-white/90 p-4 shadow-sm backdrop-blur"
                          >
                            <div className="flex items-start gap-3">
                              <span className={clsx('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', dotClass)} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-stone-700">{entry.summary}</p>
                                <p className="mt-1 text-[11px] text-stone-400">
                                  {timeAgo(entry.occurred_at)}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div
            ref={modalRef}
            className="w-full max-w-2xl overflow-y-auto rounded-t-[28px] bg-white p-6 shadow-2xl sm:rounded-[28px] sm:p-8 max-h-[90vh]"
          >
            {/* Modal header */}
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-stone-900">
                {editingNote ? 'Edit Note' : 'New Note'}
              </h2>
              <button
                onClick={closeModal}
                className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-100 hover:text-stone-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {/* Title */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Note title"
                  className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                />
              </div>

              {/* Body */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Body
                </label>
                <textarea
                  rows={4}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Write your note..."
                  className="w-full resize-none rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                />
              </div>

              {/* Row: category + source */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                  >
                    <option value="note">Note</option>
                    <option value="thought">Thought</option>
                    <option value="feature-request">Feature Request</option>
                    <option value="action-item">Action Item</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Source
                  </label>
                  <select
                    value={form.source}
                    onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                  >
                    <option value="manual">Manual</option>
                    <option value="telegram">Telegram</option>
                    <option value="melissa">Melissa</option>
                  </select>
                </div>
              </div>

              {/* Author */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Author
                </label>
                <select
                  value={form.author ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                  className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                >
                  <option value="">Unattributed</option>
                  {AUTHORS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status (edit mode only) */}
              {editingNote && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="awaiting_approval">Needs Go-Ahead</option>
                    <option value="done">Done</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </div>
              )}

              {/* Save button */}
              <button
                type="submit"
                disabled={saving}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-stone-800 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : editingNote ? 'Update Note' : 'Add Note'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesInterface;
